import { useQuery } from "@tanstack/react-query";
import { listOrgUnits } from "../api/rest";
import { useSiteStore } from "../stores/siteStore";
import { setupRole, setupSteps, setupComplete, type SetupRole, type SetupStep } from "../app/provisioning";

export interface Provisioning {
  role: SetupRole;
  steps: SetupStep[];
  complete: boolean;
  loading: boolean;
}

/**
 * Live provisioning state for the guided-setup page (ADR 175). Site and
 * warehouse counts come straight off the store (already loaded before first
 * render). Tenant count needs the org-units list, and only a global admin can
 * create tenants — so only they fetch it.
 */
export function useProvisioning(): Provisioning {
  const isGlobalAdmin = useSiteStore((s) => s.isGlobalAdmin);
  const roleRank = useSiteStore((s) => s.roleRank);
  const siteCount = useSiteStore((s) => s.availableSites.length);
  const warehouseCount = useSiteStore((s) => s.availableWarehouses.length);
  const canAdminOrgUnits = useSiteStore((s) => s.adminOrgUnits.length > 0);

  // hasSite is a grant signal, not a system-state one: availableSites is the
  // caller's resolved scope (/v1/me/sites), so "no sites" for a non-admin means
  // "granted nothing", which is exactly what separates warehouse-admin from
  // other (ADR 223).
  const role = setupRole(isGlobalAdmin, roleRank, canAdminOrgUnits, siteCount > 0);
  const orgUnits = useQuery({
    queryKey: ["org-units"],
    queryFn: listOrgUnits,
    enabled: role === "global-admin",
  });
  const tenantCount = (orgUnits.data ?? []).filter((o) => o.type === "tenant").length;

  const steps = setupSteps(role, { tenantCount, siteCount, warehouseCount });
  return {
    role,
    steps,
    complete: setupComplete(steps),
    loading: role === "global-admin" && orgUnits.isLoading,
  };
}
